// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PreviouslyPurchasedBadge, {
    PreviouslyPurchasedBadgeStyle,
} from "@vanmeter/App/Components/CustomBadges/PreviouslyPurchasedBadge";
import ProductActions, { ProductActionsStyle } from "@vanmeter/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@vanmeter/App/Components/Product/ProductGridContainer";
import QtyNumbers, { QtyNumbersStyles } from "@vanmeter/App/Components/Product/QtyNumbers";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { ProductAvailabilityStyles } from "@vanmeter/Overrides/Components/ProductAvailability";

import ProductBrand, { ProductBrandStyles } from "@vanmeter/Overrides/Components/ProductBrand";
import ProductContextAvailability from "@vanmeter/Overrides/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@vanmeter/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@vanmeter/Overrides/Components/ProductPartNumbers";
import ProductPrice from "@vanmeter/Overrides/Components/ProductPrice";
import { CarouselCardType } from "@vanmeter/XDL/types";
import { mergeWithCss } from "@vanmeter/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    productInfo:
        getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id) || createFromProduct(ownProps.product),
    // XNG-Changes
    productSettings: getSettingsCollection(state).productSettings,
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    // XNG-Changes
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
    view?: CarouselCardType;
    showAvailability?: boolean;
    showCustomBadges?: boolean;
    showManufacturerItem?: boolean;
    showCustomerName?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-changes
    productGridContainerStyles?: ProductGridContainerStyle;
    productImageBadges?: ProductImageBadgesStyles;
    miniProductImageBadges?: ProductImageBadgesStyles;
    productActions?: ProductActionsStyle;
    overlayBadge?: InjectableCss;
    customBadgeWrapper?: InjectableCss;
    qtyNumbersStyles?: QtyNumbersStyles;
    availabilityStyles?: ProductAvailabilityStyles;
    sectionWrapper?: InjectableCss;
    prevPurchasedTag?: PreviouslyPurchasedBadgeStyle;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 6px;
        `,
    },
    qtyGridItem: { width: 3 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: { width: 9 },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    // XNG-Changes
    actionsTemplate,
    productSettings,
    view,
    showAvailability,
    showCustomBadges = true,
    showManufacturerItem = true,
    showCustomerName = true,
    isTabletScreen,
}: Props) => {
    const [styles, setStyles] = useState(() => mergeWithCss(productCarouselProductStyles, extendedStyles));

    // XNG-Changes: updating styles upon view change
    useEffect(() => {
        setStyles(() => mergeWithCss(productCarouselProductStyles, extendedStyles));
    }, [view, extendedStyles]);

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
        overlayBadgeWrapperStyles,
    } = useOverlayBadge({
        productContext,
        showImageBadge: showImageBadges,
        showTextBadge: showTextBadges,
        styles,
    });
    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    // XNG-Change: handle show image casses (base image , overlay badge, custom badges).
    const hasCustomBadges = product.properties?.lastPurchasedOn;
    const hasOverlayBadge = showOverlayBadge && !!overlayBadge;
    const hasCustomBadgeUI = showCustomBadges && hasCustomBadges;
    const showBaseImage = !hasOverlayBadge && !hasCustomBadgeUI;

    return (
        <>
            <ProductContext.Provider value={productContext} data-test-selector="widget_PurchasedProducts">
                <ProductGridContainer className="slideContainer" extendedStyles={styles.productGridContainerStyles}>
                    {/* XNG-Change */}
                    <ProductGridContainer.Image>
                        {showImage && (
                            <>
                                {showBaseImage && (
                                    <ProductImage
                                        product={productContext}
                                        onClick={() => trackProductRecommendation(true)}
                                        extendedStyles={styles.productImageStyles}
                                    />
                                )}

                                {/*  XNG-Changes */}
                                {(hasCustomBadgeUI || hasOverlayBadge) && (
                                    <StyledWrapper
                                        {...styles.badgeAreaWrapper}
                                        ref={badgeAreaWrapperRef}
                                        data-test-selector="productCarouselBadge"
                                    >
                                        <ProductImage
                                            extendedStyles={styles.productImageStyles}
                                            product={productContext}
                                        />
                                        {hasCustomBadgeUI && (
                                            <PreviouslyPurchasedBadge
                                                product={productContext}
                                                {...{ view }}
                                                displayBadgeInTwoLines={
                                                    view === CarouselCardType.MiniCard ||
                                                    (view === CarouselCardType.Grid && isTabletScreen)
                                                }
                                                extendedStyles={styles.prevPurchasedTag}
                                            />
                                        )}
                                        {hasOverlayBadge && (
                                            <StyledWrapper
                                                {...overlayBadgeWrapperStyles}
                                                {...{ view }}
                                                onClick={overlayBadgeWrapperClickHandler}
                                            >
                                                <Badge
                                                    type={overlayBadge.badgeType}
                                                    value={
                                                        overlayBadge.badgeType === "Image"
                                                            ? overlayBadge.largeImageBadgePath
                                                            : overlayBadge.displayText
                                                    }
                                                    badgeTextColor={overlayBadge.textColorHexCode}
                                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                                    badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                                    imageAltText={
                                                        overlayBadge.imageAltText
                                                            ? overlayBadge.imageAltText
                                                            : overlayBadge.name
                                                    }
                                                    textBadgeCss={styles.badgeText}
                                                    imageBadgeProps={styles.badgeImageProps}
                                                />
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                )}
                            </>
                        )}
                    </ProductGridContainer.Image>
                    <ProductGridContainer.Brand>
                        {showBrand && <ProductBrand brand={productContext.product.brand} />}
                    </ProductGridContainer.Brand>
                    <ProductGridContainer.Title>
                        <ProductDescription
                            product={productContext}
                            onClick={() => {
                                trackProductRecommendation(true);
                            }}
                            extendedStyles={styles.productDescriptionStyles}
                            {...{ showDescription: false, showTitle }}
                        />
                    </ProductGridContainer.Title>
                    <ProductGridContainer.Description>
                        <ProductDescription
                            product={productContext}
                            onClick={() => {
                                trackProductRecommendation(true);
                            }}
                            extendedStyles={styles.productDescriptionStyles}
                            {...{ showTitle: false }}
                        />
                    </ProductGridContainer.Description>
                    <ProductGridContainer.PartNumbers>
                        {showPartNumbers && (
                            <ProductPartNumbers
                                productNumber={productContext.product.productNumber}
                                customerProductNumber={productContext.product.customerProductNumber}
                                manufacturerItem={productContext.product.manufacturerItem}
                                upcCode={productContext.product.detail?.upcCode}
                                showCustomerName={showCustomerName}
                                showManufacturerItem={showManufacturerItem}
                            />
                        )}
                        {/* XNG-Changes: */}
                        {view && (view === CarouselCardType.Grid || view === CarouselCardType.MiniCard) && (
                            <QtyNumbers
                                extendedStyles={styles.qtyNumbersStyles}
                                minimumOrderQty={productContext.product.minimumOrderQty}
                            />
                        )}
                    </ProductGridContainer.PartNumbers>

                    {/* XNG-Changes: add availability */}
                    <ProductGridContainer.Availability>
                        {showAvailability && productSettings.showInventoryAvailability && (
                            <ProductContextAvailability extendedStyles={styles.availabilityStyles} {...{ view }} />
                        )}
                    </ProductGridContainer.Availability>
                    <ProductGridContainer.Badges>
                        {showImageBadges && (
                            <ProductImageBadges
                                badgeList={imageBadges || []}
                                maxBadges={maximumNumberOfImageBadges ?? 4}
                            />
                        )}
                        {showTextBadges && textBadges && (
                            <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                        )}
                    </ProductGridContainer.Badges>
                    <ProductGridContainer.Price>
                        {showPrice && (
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showSalePriceLabel={false}
                                // XNG-Changes:
                                showPricingUom={
                                    view && (view === CarouselCardType.Grid || view === CarouselCardType.MiniCard)
                                }
                            />
                        )}
                    </ProductGridContainer.Price>
                    {/* XNG-Changes: add product actions */}
                    <ProductGridContainer.AddToCartActions>
                        {showAddToCart && <ProductActions extendedStyles={styles.productActions} />}
                    </ProductGridContainer.AddToCartActions>
                    <ProductGridContainer.AddToList>
                        {actionsTemplate ? actionsTemplate(productContext.product) : null}
                        {showAddToList && (
                            <ProductAddToListLink
                                data-test-selector="addToListLink"
                                extendedStyles={styles.productAddToListLinkStyles}
                            />
                        )}
                    </ProductGridContainer.AddToList>
                </ProductGridContainer>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
